import { useEffect, useState, useCallback } from "react";
import { authClient } from "@/integrations/supabase/authClient";
import type { Session } from "@supabase/auth-js";
import {
  buildInbody,
  type InbodyData,
  type InbodyScanRow,
} from "./buildInbody";
// Walk-in and booked entry timestamps are instants — sastTime renders them in
// SAST, which is what members read everywhere else in the app.
import { buildVisitSessions, visitTimesFor, sastTime } from "./visitTimes";
import { stackLabel } from "./bookingLogic";

/**
 * Which parts of the account load came back as an error rather than as data.
 *
 * Every list below defaults to empty, so without this a failed read is
 * indistinguishable from a member who genuinely has nothing — and the app
 * would tell someone with bookings that they have none. Screens read these
 * to say "couldn't load" instead, and to offer a retry.
 */
/** True when a Supabase read came back carrying an error. */
function readFailed(res: { error?: unknown } | null | undefined): boolean {
  return !!(res && res.error);
}

export type ZuzuLoadErrors = {
  bookings: boolean;
  scans: boolean;
  visits: boolean;
  invoices: boolean;
  credits: boolean;
};

export const NO_LOAD_ERRORS: ZuzuLoadErrors = {
  bookings: false,
  scans: false,
  visits: false,
  invoices: false,
  credits: false,
};

export type ZuzuSession = {
  loading: boolean;
  /** Per-area read failures — see ZuzuLoadErrors. */
  loadErrors: ZuzuLoadErrors;
  session: Session | null;
  userId: string | null;
  name: string;
  initials: string;
  email: string;
  mobile: string;
  /** True once the member's mobile passed the SMS OTP check
   *  (profiles.phone_verified). Mobile is the PRIMARY verification — it gates
   *  entry to the app (see VerifyGate). */
  phoneVerified: boolean;
  /** True once the member's email is confirmed (Supabase confirm link or the
   *  verify-email OTP). OPTIONAL — surfaced in the profile, gates nothing. */
  emailVerified: boolean;
  /**
   * True once the profiles row behind phoneVerified/phoneVerified has actually
   * been read. Until then both flags are still at their `false` defaults, which
   * is indistinguishable from "verified nothing" — so anything deciding on them
   * (the verify gate) must wait for this rather than act on the default.
   */
  profileLoaded: boolean;
  dob: string;
  /** Raw ISO birth date (YYYY-MM-DD) for the profile's date input. */
  dobIso: string;
  /** What the door reader calls this member, or '' to use their registered
   * name. Display only — never used for billing or the audit trail. */
  preferredName: string;
  /** Profile gender ('female' | 'male') or '' — needed for InBody scans. */
  gender: string;
  /** Height in cm, or null — needed for InBody scans. */
  heightCm: number | null;
  avatar: string;
  credits: number;
  /** Design plan id: 'monthly' when an active subscription exists, else null. */
  plan: "monthly" | null;
  /** Billing cadence of the active membership, for display. */
  planBilling: "monthly" | "annual" | null;
  /**
   * Which membership: "off-peak" (peak minutes cost credits) or "anytime"
   * (the founding membership — peak included). Null without one.
   */
  planType: "off-peak" | "anytime" | null;
  /** ISO end of a founding member's 12-month contract (null otherwise). */
  planCommitmentEnd: string | null;
  /** ISO end/renewal date of the active membership, for display. */
  planRenews: string | null;
  /**
   * Does the active membership actually renew itself? Read from the
   * subscription, not assumed: recurring billing is behind
   * PAYFAST_RECURRING_ENABLED, so a membership bought while that was off is a
   * one-off that ENDS on planRenews rather than renewing on it — and the two
   * need different words in front of the same date.
   */
  planAutoRenew: boolean;
  /** Latest InBody score, or null when the member has no scans. */
  inbodyScore: number | null;
  /** Full InBody breakdown for the results panel, or null when no scans. */
  inbodyData: InbodyData | null;
  /** Upcoming bookings mapped to the design's {id, what, when, kind} shape. */
  upcoming: ZuzuUpcoming[];
  /** Real purchase history for the wallet/profile invoices list. */
  invoices: ZuzuInvoice[];
  /** Promo codes and gift cards this member has redeemed. */
  redeemedCodes: ZuzuRedeemedCode[];
  /** Past bookings for the profile session-history list. */
  visits: ZuzuVisit[];
  /** Free units right now per modality id (plunge/sauna/comp/red/inbody). */
  liveFree: Record<string, number>;
  refresh: () => void;
  signOut: () => Promise<void>;
};

export type ZuzuInvoice = {
  no: string;
  desc: string;
  date: string;
  amount: number;
  kind: string;
};

export type ZuzuRedeemedCode = {
  /** 'promo' | 'gift' — which kind of code was entered. */
  source: string;
  code: string;
  /** What it granted, already worded for display. */
  grants: string;
  date: string;
};

export type ZuzuVisit = {
  what: string;
  date: string;
  tin: string;
  tout: string;
  kind: string;
  /** Actual scan-in/out times (SAST, "HH:MM") from the entry session, or null
   * when the member never scanned in / the session never ended. */
  scanIn: string | null;
  scanOut: string | null;
  /** The nightly sweep marked this booking a no-show (never scanned in). */
  noShow: boolean;
  /** The member cancelled this booking before it ran. */
  cancelled: boolean;
  /** Net credits this visit cost — charges minus any refunds (cancellation,
   * early leave). Null → free on the unlimited plan / unknown. For a
   * cancelled booking this is the cancellation fee that was kept. */
  credits: number | null;
  /** When the member cancelled, from the refund ledger row's timestamp —
   * null for a 100%-fee cancellation (inside 12h there's no refund row). */
  cancelledOn?: string | null;
};

export type ZuzuUpcoming = {
  id: string;
  what: string;
  when: string;
  time: string;
  kind: string;
  /** Real DB booking_type ('machine' | 'room'), for cancellation. */
  type: string;
  /** Local ISO start datetime (YYYY-MM-DD HH:MM:SS) for live countdowns. */
  startISO: string;
  /** Local ISO end datetime, used to beep at the end of each machine usage. */
  endISO: string;
  /** Raw machine_id ('plunge-1' | 'sauna' | …), null for room bookings. */
  machine: string | null;
  /**
   * How many booking rows this entry covers. 1 for a single station or a room;
   * more for a stack, which is charged and cancelled as one group and so is
   * listed as one entry. Optional: absent means one.
   */
  steps?: number;
  /**
   * Every step's own end, for things that care about the stations rather than
   * the booking — the end-of-machine beeps cue a member to move on, so a stack
   * needs one per station even though it is listed as a single booking.
   * Absent means the entry's own endISO is the only one.
   */
  stepEnds?: { endISO: string; machine: string | null }[];
  /** Each station's own booked window, in running order — what the edit
   *  screen re-times. The group is billing; these are the schedule. */
  stations?: {
    id: string;
    machine: string | null;
    start: string;
    end: string;
  }[];
  /**
   * When the member has already used this booking's one move: the original
   * start, as the database remembers it. Null or absent means it can still
   * be moved (if it is the kind of booking that can be).
   */
  movedFrom?: string | null;
};

/* "14 mar 1991" from an ISO date string. */
export function formatDob(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return "";
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/* Map a Supabase booking onto the design's room kind + human label. Machine
   bookings carry the modality in machine_id ('circuit' marks a multi-room
   session); room/loop are identified by booking_type. */
export function mapBookingType(
  type: string,
  machineId?: string | null,
): { kind: string; what: string } {
  const t = (type || "").toLowerCase();
  if (t === "room") return { kind: "room", what: "private room" };
  if (t === "loop") return { kind: "session", what: "recovery circuit" };
  const m = (machineId || type || "").toLowerCase();
  if (m === "circuit") return { kind: "session", what: "recovery circuit" };
  if (m.includes("plunge") || m.includes("cold"))
    return { kind: "plunge", what: "cold plunge" };
  if (m.includes("sauna")) return { kind: "sauna", what: "nordic sauna" };
  if (m.includes("comp")) return { kind: "comp", what: "compression" };
  if (m.includes("red") || m.includes("light"))
    return { kind: "red", what: "red light" };
  if (m.includes("inbody") || m.includes("scan"))
    return { kind: "inbody", what: "inbody scan" };
  if (m.includes("room") || m.includes("private"))
    return { kind: "room", what: "private room" };
  return {
    kind: "session",
    what: m ? m.replace(/_/g, " ") : "recovery session",
  };
}

// Design modality id → booking machine_id, and room capacity.
const MACHINE_OF: Record<string, string> = {
  plunge: "plunge",
  sauna: "sauna",
  comp: "compression",
  red: "redlight",
  inbody: "inbody",
};
const CAPACITY: Record<string, number> = {
  plunge: 2,
  sauna: 8,
  comp: 1,
  red: 1,
  inbody: 1,
};

/** How often the "N free now" counts are re-read while the app is in front. */
const LIVE_CAPACITY_POLL_MS = 60_000;

/**
 * The full Postgrest/Realtime/Storage client, loaded once and shared.
 *
 * It's imported on demand rather than at the top of this module because this
 * hook runs on every marketing-page render, and an anonymous visitor shouldn't
 * download the whole SDK to find out nobody's signed in (see authClient.ts).
 *
 * One promise, not an `await import()` per call site: three effects here can
 * want the client in the same tick, and issuing three concurrent imports of the
 * same module is both wasteful and, under vite-node, racy — two overlapping
 * imports of a mocked module left one of them pending forever, which showed up
 * as the account fetch silently never completing.
 */
let supabaseModule: Promise<
  typeof import("@/integrations/supabase/client")
> | null = null;
const loadSupabase = async () => {
  if (!supabaseModule)
    supabaseModule = import("@/integrations/supabase/client");
  return (await supabaseModule).supabase;
};

const WEEKDAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const MONTHS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];

/* "today · 07:30 - 09:00" / "sat 10 may · 18:00 - 19:00" */
export function formatWhen(
  dateStr: string,
  start: string,
  end: string,
): string {
  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const tomorrow = new Date(today.getTime() + 86400000)
    .toISOString()
    .slice(0, 10);
  let day: string;
  if (dateStr === todayStr) day = "today";
  else if (dateStr === tomorrow) day = "tomorrow";
  else {
    const d = new Date(dateStr + "T00:00:00");
    day = `${WEEKDAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
  }
  const t = (s: string) => (s || "").slice(0, 5);
  return `${day} · ${t(start)} - ${t(end)}`;
}

/* "14 mar" from an ISO date or timestamp. */
function shortDate(s: string): string {
  const d = new Date(s);
  if (isNaN(d.getTime())) return "";
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

export function initialsFrom(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "zu";
  if (parts.length === 1) return parts[0].slice(0, 2).toLowerCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toLowerCase();
}

/** Live Supabase session + the account data the redesign app screens need. */
export function useZuzuSession(
  opts: {
    /**
     * Poll live room capacity even when nobody is signed in. Set by the pages
     * that actually render a "N open now" count (/book, the equipment pages),
     * so an ordinary marketing visit doesn't load the Postgrest client — and
     * so the pages that DO show a count show a real one.
     */
    liveCapacity?: boolean;
  } = {},
): ZuzuSession {
  const { liveCapacity = false } = opts;
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState<ZuzuLoadErrors>(NO_LOAD_ERRORS);
  const [session, setSession] = useState<Session | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [dob, setDob] = useState("");
  const [dobIso, setDobIso] = useState("");
  const [preferredName, setPreferredName] = useState("");
  const [gender, setGender] = useState("");
  const [heightCm, setHeightCm] = useState<number | null>(null);
  const [avatar, setAvatar] = useState("");
  const [credits, setCredits] = useState(0);
  const [plan, setPlan] = useState<"monthly" | null>(null);
  const [planBilling, setPlanBilling] = useState<"monthly" | "annual" | null>(
    null,
  );
  const [planRenews, setPlanRenews] = useState<string | null>(null);
  const [planType, setPlanType] = useState<"off-peak" | "anytime" | null>(null);
  const [planCommitmentEnd, setPlanCommitmentEnd] = useState<string | null>(
    null,
  );
  const [planAutoRenew, setPlanAutoRenew] = useState(false);
  const [inbodyScore, setInbodyScore] = useState<number | null>(null);
  const [inbodyData, setInbodyData] = useState<InbodyData | null>(null);
  const [upcoming, setUpcoming] = useState<ZuzuUpcoming[]>([]);
  const [invoices, setInvoices] = useState<ZuzuInvoice[]>([]);
  const [redeemedCodes, setRedeemedCodes] = useState<ZuzuRedeemedCode[]>([]);
  const [visits, setVisits] = useState<ZuzuVisit[]>([]);
  const [liveFree, setLiveFree] = useState<Record<string, number>>({});
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  // Track auth session (initial + changes). Uses the lightweight auth-only
  // client (see authClient.ts) rather than the full Postgrest/Realtime/
  // Storage client — this hook runs on every marketing-page render via
  // ZuzuRedesign, so anonymous visitors shouldn't pay for the whole SDK just
  // to find out nobody's signed in.
  useEffect(() => {
    authClient.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = authClient.onAuthStateChange((_e, s) => {
      setSession(s);
      setLoading(false);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // Re-fetch account data whenever the app regains the foreground — in the
  // native app the PayFast checkout completes in the system browser, so the
  // new credits / membership only show up if we refresh when the member
  // switches back. Same for a web tab that was backgrounded during payment.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  // Keep the "N free now" counts live.
  //
  // Two things were wrong here. The obvious one: the poll was gated on a
  // signed-in user, so the ONE page whose job is "see what's actually open
  // before you have an account" (/book) fell through to the hardcoded
  // ZZ_LIVE_DEFAULTS and showed a signed-out visitor invented numbers.
  // get_live_capacity is granted to anon precisely so it doesn't have to.
  //
  // The quieter one: `supabase` was never imported into this module, so every
  // tick threw ReferenceError and the counts never moved for signed-in members
  // either. The full Postgrest client is deliberately kept out of the eager
  // marketing bundle (see the fetch effect below), so it's imported on demand
  // here the same way.
  //
  // `liveCapacity` lets a signed-out page opt in — the pages that actually
  // render a capacity count — so an ordinary marketing visit still doesn't pay
  // for the client. Skipped while the tab is hidden; the visibilitychange
  // refresh above catches the member up when they come back.
  const wantsLiveCapacity = !!session?.user?.id || !!liveCapacity;
  useEffect(() => {
    if (!wantsLiveCapacity) return;
    let cancelled = false;
    const pull = async () => {
      if (document.visibilityState !== "visible") return;
      // A missed poll is not worth surfacing: the counts stay as they are and
      // the next tick catches up. Wrapped so a transient failure can never
      // become an unhandled rejection — this fires on a timer, so there is no
      // caller to catch it.
      try {
        // Loaded on demand (see loadSupabase) so an ordinary marketing visit
        // doesn't pull in the full Postgrest/Realtime/Storage client. Unlike on
        // main, this effect CAN run with nobody signed in — /book shows live
        // capacity to signed-out visitors — so the chunk isn't always warm.
        const supabase = await loadSupabase();
        if (cancelled) return;
        const { data, error } = await supabase.rpc("get_live_capacity");
        if (cancelled || error || !data) return;
        const byMachine = data as Record<string, number>;
        const free: Record<string, number> = {};
        Object.keys(CAPACITY).forEach((id) => {
          free[id] = byMachine[MACHINE_OF[id]] ?? CAPACITY[id];
        });
        setLiveFree((prev) =>
          Object.keys(CAPACITY).every((id) => prev[id] === free[id])
            ? prev // unchanged → don't churn the app's remount key
            : free,
        );
      } catch {
        // Offline, or the chunk failed to load — the counts stay as they
        // are and the next tick tries again.
      }
    };
    // Pull once immediately: a signed-out visitor has no account fetch to
    // seed the counts, so waiting a full minute would show them the stale
    // defaults for exactly as long as they're deciding whether to sign up.
    void pull();
    const timer = window.setInterval(pull, LIVE_CAPACITY_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [wantsLiveCapacity]);

  // Catch-up sync of InBody results. The post-scan sync in ZuzuAppRuntime
  // only runs when a scan completes through the app — a scan finished after a
  // bridge hiccup (or synced late on the LookinBody side) never reached the
  // profile. sync-inbody-results pulls the member's FULL history from the
  // bridge and upserts every missing row, so running it once per app session
  // guarantees all results eventually land. Throttled per user so backgrounded
  // tabs / quick relaunches don't hammer the bridge tunnel.
  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId) return;
    const key = `zz_ib_synced_at_${userId}`;
    const last = Number(localStorage.getItem(key) || 0);
    if (Date.now() - last < 15 * 60 * 1000) return;
    localStorage.setItem(key, String(Date.now()));
    (async () => {
      try {
        const supabase = await loadSupabase();
        const { data } = await supabase.functions.invoke(
          "sync-inbody-results",
          { body: {} },
        );
        const d = data as { synced_count?: number } | null;
        // New rows arrived → re-fetch so the profile shows them right away.
        if ((d?.synced_count ?? 0) > 0) refresh();
      } catch {
        // Bridge offline — the next session (or post-scan sync) catches up.
      }
    })();
  }, [session?.user?.id, refresh]);

  // Load account data once we have a user.
  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId) {
      setName("");
      setEmail("");
      setMobile("");
      setPhoneVerified(false);
      setEmailVerified(false);
      setProfileLoaded(false);
      setDob("");
      setDobIso("");
      setPreferredName("");
      setGender("");
      setHeightCm(null);
      setAvatar("");
      setCredits(0);
      setPlan(null);
      setPlanBilling(null);
      setPlanRenews(null);
      setPlanType(null);
      setPlanCommitmentEnd(null);
      setInbodyScore(null);
      setInbodyData(null);
      setUpcoming([]);
      setInvoices([]);
      setRedeemedCodes([]);
      setVisits([]);
      setLiveFree({});
      // Signed out: the lists are empty because there is no account to read,
      // not because a read failed.
      setLoadErrors(NO_LOAD_ERRORS);
      return;
    }
    let cancelled = false;
    const todayStr = new Date().toISOString().slice(0, 10);

    const meta = session.user.user_metadata || {};
    // Auth metadata only. The email's local part used to be the last fallback
    // here, which greeted a member with no metadata name as "good morning,
    // signed-in-1788761267346" — and worse, it filled `name` before the
    // profiles query below could, whose `prev || …` then kept the email
    // fragment forever. Better no name than someone's address read back at
    // them: __ZZ_FIRST() says "there" and initialsFrom() says "zu".
    const metaName: string = meta.full_name || meta.first_name || "";
    if (metaName) setName(metaName);
    setEmail(session.user.email || "");
    if (meta.phone_number) setMobile(meta.phone_number);
    // A confirmed email is already on the session, so settle that half of the
    // verification question here — synchronously, in the same commit that makes
    // the session available. Waiting for the profiles query below meant the app
    // rendered one frame with both flags still false, which flashed the verify
    // gate at members who had verified all along.
    if (session.user.email_confirmed_at) setEmailVerified(true);

    (async () => {
      // Loaded on demand (see loadSupabase): this branch only runs once a
      // session actually exists, so anonymous marketing-page visitors never
      // download the full client.
      const supabase = await loadSupabase();
      if (cancelled) return;
      const profilePromise = supabase
        .from("profiles")
        .select(
          "first_name, preferred_name, phone_number, birth_date, avatar_url, gender, height_cm, phone_verified, email_verified",
        )
        .eq("user_id", userId)
        .maybeSingle();

      // The verify gate blocks the whole app on these two flags, so apply them
      // the moment the profile lands instead of waiting on the slowest of the
      // account queries below (400 scans, 50 transactions, live capacity…).
      // Same promise, awaited twice — no extra request.
      void profilePromise.then(({ data }) => {
        if (cancelled) return;
        const p = data as {
          phone_number?: string | null;
          phone_verified?: boolean | null;
          email_verified?: boolean | null;
        } | null;
        // The number comes with them. It used to be set only in the slow pass
        // below, after every account query had resolved — so the gate could
        // render, on these very flags, with an empty number in it. A member
        // who tapped "sms me the code" in that window was told their own
        // number was invalid. Unblocking the gate and handing it the number
        // it needs have to happen together.
        if (p?.phone_number) setMobile(p.phone_number);
        setPhoneVerified(!!p?.phone_verified);
        setEmailVerified(
          !!p?.email_verified || !!session?.user?.email_confirmed_at,
        );
        setProfileLoaded(true);
      });

      const [
        profileRes,
        creditRes,
        subRes,
        inbodyRes,
        bookingsRes,
        liveRes,
        txRes,
        historyRes,
        walkinRes,
        entryRes,
        ledgerRes,
        codesRes,
      ] = await Promise.all([
        profilePromise,
        supabase
          .from("user_credits")
          .select("balance")
          .eq("user_id", userId)
          .maybeSingle(),
        supabase.rpc("get_active_subscription", { p_user_id: userId }),
        supabase
          .from("inbody_scans")
          .select("*")
          .eq("user_id", userId)
          // Scans the member hid from their profile stay out of the score,
          // charts and trends (the column is NOT NULL DEFAULT false, so a
          // plain equality covers every row).
          .eq("hidden", false)
          .order("scan_date", { ascending: true })
          // enough history for the year range on the score chart
          .limit(400),
        supabase
          .from("bookings")
          .select(
            "id, booking_type, booking_date, start_time, end_time, status, machine_id, charge_group_id, moved_from",
          )
          .eq("user_id", userId)
          .eq("status", "confirmed")
          .gte("booking_date", todayStr)
          .order("booking_date", { ascending: true })
          .order("start_time", { ascending: true })
          .limit(40),
        // Live "right now" free units per machine — RLS-safe aggregate RPC
        // (a direct cross-member query would only return this user's rows).
        supabase.rpc("get_live_capacity"),
        // Real purchase history → invoices list (RLS: own rows only).
        supabase
          .from("transactions")
          .select(
            "transaction_type, amount, description, invoice_number, created_at",
          )
          .eq("user_id", userId)
          .order("created_at", { ascending: false })
          .limit(50),
        // Past bookings → visit history. no_show and cancelled rows are kept
        // so the list can say so (record_no_shows sets no_show nightly).
        supabase
          .from("bookings")
          .select(
            "id, booking_type, machine_id, booking_date, start_time, end_time, status, charge_group_id",
          )
          .eq("user_id", userId)
          .in("status", ["confirmed", "completed", "no_show", "cancelled"])
          .lte("booking_date", todayStr)
          .order("booking_date", { ascending: false })
          .order("start_time", { ascending: false })
          .limit(50),
        // Ended walk-in sessions (timer/face-entry visits without a booking) —
        // these live in active_sessions, not bookings, so the history list
        // needs both. Booked sessions are excluded here to avoid showing the
        // same visit twice.
        supabase
          .from("active_sessions")
          .select("started_at, ended_at, booking_id, credits_deducted")
          .eq("user_id", userId)
          .not("ended_at", "is", null)
          .is("booking_id", null)
          .order("started_at", { ascending: false })
          .limit(50),
        // Entry sessions → the ACTUAL scan-in/out times for the history list
        // (the booking row only has the reserved window). NOT filtered to rows
        // carrying a booking_id: the door binds a visit to ONE booking (the
        // earliest of the day, room first — see _zz_open_session), and a member
        // who walked in more than five minutes early gets no booking_id at all,
        // so matching on that column alone left most booked visits blank. The
        // matching below falls back to the charge group, then to the clock.
        supabase
          .from("active_sessions")
          .select("booking_id, started_at, ended_at")
          .eq("user_id", userId)
          .order("started_at", { ascending: false })
          .limit(200),
        // Credits each booking cost, from the ledger (bookings themselves
        // don't carry it). Charges AND refunds, so a cancelled or left-early
        // booking shows its net cost, not the original charge.
        supabase
          .from("credit_ledger")
          .select("source_id, amount, created_at")
          .eq("user_id", userId)
          .not("source_id", "is", null)
          .order("created_at", { ascending: false })
          .limit(200),
        // Promo codes and gift cards this member redeemed. A redeemed code
        // never writes a transactions row, so the billing screen can't find it
        // in the invoice history — and a membership promo writes no ledger row
        // either, hence a definer RPC over both code tables.
        supabase.rpc("get_my_redeemed_codes"),
      ]);
      if (cancelled) return;
      // A failed read leaves .data null, which every mapping below turns into
      // an empty list — the same shape a member with no history has. Record
      // which reads actually failed so the screens can tell the two apart.
      setLoadErrors({
        bookings: readFailed(bookingsRes),
        scans: readFailed(inbodyRes),
        visits:
          readFailed(historyRes) ||
          readFailed(walkinRes) ||
          readFailed(entryRes),
        invoices:
          readFailed(txRes) || readFailed(ledgerRes) || readFailed(codesRes),
        credits: readFailed(creditRes),
      });
      const prof = profileRes.data as {
        first_name?: string;
        phone_number?: string;
        birth_date?: string;
        avatar_url?: string;
        preferred_name?: string | null;
        gender?: string | null;
        height_cm?: number | null;
        phone_verified?: boolean | null;
        email_verified?: boolean | null;
      } | null;
      // `prev ||` so a full name already in auth metadata (which carries a
      // surname, and so real initials) is not replaced by the first name
      // alone. It is now only ever a real name on either side.
      if (prof?.first_name) setName((prev) => prev || prof.first_name!);
      // The profiles row wins over auth metadata for mobile: phone_number is
      // OTP-verified and service-role-written, while the session's metadata
      // copy can be stale until the next token refresh.
      if (prof?.phone_number) setMobile(prof.phone_number);
      // phoneVerified / emailVerified are set above, as soon as the profile
      // resolves — a Supabase confirmation link counts as email verification
      // too, since the OTP flow (profiles.email_verified) is only for projects
      // with the "Confirm email" setting off.
      if (prof?.birth_date) setDob(formatDob(prof.birth_date));
      setDobIso(prof?.birth_date || "");
      setPreferredName(prof?.preferred_name || "");
      setGender(prof?.gender || "");
      setHeightCm(prof?.height_cm ?? null);
      setAvatar(prof?.avatar_url || "");
      setCredits((creditRes.data as { balance?: number } | null)?.balance ?? 0);
      const subData = subRes.data as {
        has_subscription?: boolean;
        billing_period?: "monthly" | "annual";
        plan_type?: string;
        commitment_end?: string | null;
        end_date?: string;
        auto_renew?: boolean;
      } | null;
      const hasSub = !!subData?.has_subscription;
      setPlan(hasSub ? "monthly" : null);
      setPlanBilling(hasSub ? (subData?.billing_period ?? "monthly") : null);
      setPlanType(
        hasSub
          ? subData?.plan_type === "anytime"
            ? "anytime"
            : "off-peak"
          : null,
      );
      setPlanCommitmentEnd(hasSub ? (subData?.commitment_end ?? null) : null);
      setPlanRenews(hasSub ? (subData?.end_date ?? null) : null);
      setPlanAutoRenew(hasSub && subData?.auto_renew === true);
      const scanRows = (inbodyRes.data as InbodyScanRow[] | null) || [];
      const built = buildInbody(scanRows);
      setInbodyData(built);
      const latestScore = scanRows.length
        ? scanRows[scanRows.length - 1].inbody_score
        : null;
      setInbodyScore(latestScore ?? null);

      type BookingRow = {
        id: string;
        booking_type: string;
        booking_date: string;
        start_time: string;
        end_time: string;
        machine_id: string | null;
        charge_group_id: string | null;
        moved_from?: string | null;
      };
      // Live "right now" free units, keyed by machine_id, from get_live_capacity.
      const liveByMachine =
        (liveRes.data as Record<string, number> | null) || {};
      const free: Record<string, number> = {};
      Object.keys(CAPACITY).forEach((id) => {
        const m = MACHINE_OF[id]; // design id → machine_id (comp→compression…)
        free[id] = liveByMachine[m] ?? CAPACITY[id];
      });
      setLiveFree(free);

      const rows = (bookingsRes.data as BookingRow[] | null) || [];
      // Drop bookings that have already finished earlier today — the query
      // filters by date only, so a 07:30–09:00 slot would otherwise still show
      // as "upcoming" at 11:00.
      const nowTime = new Date().toTimeString().slice(0, 8); // HH:MM:SS local
      const futureRows = rows.filter(
        (b) =>
          b.booking_date > todayStr ||
          (b.booking_date === todayStr && b.end_time > nowTime),
      );
      // A stack is stored as one row per step but was charged once and now
      // cancels as one group, so it has to be LISTED once too — otherwise the
      // member sees three bookings, cancels one, and all three disappear.
      // Collapse each charge group into a single entry spanning its first
      // start to its last end.
      const groups: BookingRow[][] = [];
      const groupIndex: Record<string, number> = {};
      futureRows.forEach((b) => {
        const key = b.charge_group_id || b.id;
        if (groupIndex[key] === undefined) {
          groupIndex[key] = groups.length;
          groups.push([b]);
        } else {
          groups[groupIndex[key]].push(b);
        }
      });

      setUpcoming(
        groups.map((steps) => {
          const first = steps[0];
          const last = steps.reduce(
            (acc, s) => (s.end_time > acc.end_time ? s : acc),
            first,
          );
          const t = (s: string) => (s || "").slice(0, 5);
          // A single booking keeps its own label; a multi-step group is a
          // stack, named for the stations it runs rather than any one of them.
          const { kind, what } =
            steps.length === 1
              ? mapBookingType(first.booking_type, first.machine_id)
              : {
                  kind: "session" as const,
                  what: stackLabel(steps.map((s) => s.machine_id)),
                };
          return {
            id: first.id,
            what,
            when: formatWhen(
              first.booking_date,
              first.start_time,
              last.end_time,
            ),
            time: `${t(first.start_time)} – ${t(last.end_time)}`,
            kind,
            type: first.booking_type,
            startISO: `${first.booking_date}T${first.start_time}`,
            endISO: `${first.booking_date}T${last.end_time}`,
            machine: first.machine_id,
            steps: steps.length,
            stepEnds: steps.map((s) => ({
              endISO: `${s.booking_date}T${s.end_time}`,
              machine: s.machine_id,
            })),
            // Each station's own window, in running order. The group is a
            // billing construct; these are what is actually scheduled, and
            // what the edit screen re-times one at a time.
            stations: steps.map((s) => ({
              id: s.id,
              machine: s.machine_id,
              start: s.start_time,
              end: s.end_time,
            })),
            // Set on every row of a moved group; any one of them says so.
            movedFrom: steps.find((s) => s.moved_from)?.moved_from ?? null,
          };
        }),
      );

      // Invoices from real transactions.
      const txKind: Record<string, string> = {
        subscription: "membership",
        credit_purchase: "credits",
        room_booking: "room",
        gift_purchase: "gift",
        promo_redemption: "promo",
      };
      const txRows =
        (txRes.data as
          | {
              transaction_type: string;
              amount: number;
              description: string;
              invoice_number: string | null;
              created_at: string;
            }[]
          | null) || [];
      setInvoices(
        txRows.map((tx) => ({
          no: tx.invoice_number || "—",
          desc: tx.description || txKind[tx.transaction_type] || "purchase",
          date: shortDate(tx.created_at),
          amount: tx.amount,
          kind: txKind[tx.transaction_type] || "other",
        })),
      );

      const codeRows =
        ((codesRes.data as { codes?: unknown } | null)?.codes as
          | {
              source: string;
              code: string;
              kind: string;
              credits: number | null;
              membership_days: number | null;
              redeemed_at: string;
            }[]
          | undefined) || [];
      setRedeemedCodes(
        codeRows.map((c) => {
          const credits = c.credits || 0;
          const days = c.membership_days || 0;
          const parts = [
            days ? `${days} days membership` : null,
            credits ? `${credits} credit${credits === 1 ? "" : "s"}` : null,
          ].filter(Boolean);
          return {
            source: c.source,
            code: c.code,
            grants: parts.length ? parts.join(" + ") : c.kind,
            date: shortDate(c.redeemed_at),
          };
        }),
      );

      // Visit history from past bookings (already finished).
      const histRows =
        (historyRes.data as
          | {
              id: string;
              booking_type: string;
              machine_id: string | null;
              booking_date: string;
              start_time: string;
              end_time: string;
              status: string;
              charge_group_id: string | null;
            }[]
          | null) || [];
      const t5 = (s: string) => (s || "").slice(0, 5);

      // Which visit each booked row actually was — see visitTimes.ts for why
      // the booking_id on the session isn't enough on its own.
      const groupOfBooking: Record<string, string> = {};
      histRows.forEach((b) => {
        groupOfBooking[b.id] = b.charge_group_id || b.id;
      });
      const visitSessions = buildVisitSessions(
        (
          (entryRes.data as
            | {
                booking_id: string | null;
                started_at: string;
                ended_at: string | null;
              }[]
            | null) || []
        ).map((row) => ({
          bookingId: row.booking_id,
          startedAt: row.started_at,
          endedAt: row.ended_at,
        })),
        groupOfBooking,
      );

      // booking id → NET credits it cost (charges minus refunds — so a
      // cancelled or left-early booking shows what it actually cost). A
      // session's single ledger row hangs off the FIRST booking of the chain,
      // so per-row lookups still find it.
      const creditsByBooking: Record<string, number> = {};
      // booking id → when its cancellation refund landed. The refund row's
      // timestamp IS the moment the member cancelled — bookings carry no
      // cancelled_at of their own. (A 100%-fee cancellation writes no refund
      // row, so it stays undated; the kept charge still shows as the fee.)
      const cancelDateByBooking: Record<string, string> = {};
      (
        (ledgerRes.data as
          | { source_id: string; amount: number; created_at?: string }[]
          | null) || []
      ).forEach((l) => {
        creditsByBooking[l.source_id] =
          (creditsByBooking[l.source_id] || 0) + -l.amount;
        if (l.amount > 0 && l.created_at && !cancelDateByBooking[l.source_id])
          cancelDateByBooking[l.source_id] = shortDate(l.created_at);
      });
      Object.keys(creditsByBooking).forEach((id) => {
        if (creditsByBooking[id] <= 0) delete creditsByBooking[id];
      });

      // A stack is one purchase stored as one row per station, so the history
      // has to LIST it once — the same collapse the upcoming list does above.
      //
      // Listed per row it read as six visits where only the first cost
      // anything: the session's single ledger row hangs off its first booking,
      // so "inbody scan · 2 credits" sat above five blank rows and looked
      // exactly like an extra charge for the scan. The cost belongs to the
      // whole 12:21–13:17 session, not to its opening five minutes.
      const histGroups: (typeof histRows)[] = [];
      const histIndex: Record<string, number> = {};
      histRows.forEach((b) => {
        const key = b.charge_group_id || b.id;
        if (histIndex[key] === undefined) {
          histIndex[key] = histGroups.length;
          histGroups.push([b]);
        } else {
          histGroups[histIndex[key]].push(b);
        }
      });

      const bookingVisits = histGroups
        .map((rows) =>
          [...rows].sort((a, b) => a.start_time.localeCompare(b.start_time)),
        )
        .filter((steps) => {
          const last = steps[steps.length - 1];
          return (
            // A cancelled booking joins the history the moment it's cancelled
            // — its fee (charged by cancellation timing) shouldn't wait for
            // the original slot to pass before it shows.
            steps.some((s) => s.status === "cancelled") ||
            last.booking_date < todayStr ||
            (last.booking_date === todayStr && last.end_time <= nowTime)
          );
        })
        .map((steps) => {
          const first = steps[0];
          const last = steps[steps.length - 1];
          // A single booking keeps its own label; a stack is named for the
          // stations it ran, matching how the upcoming list names it.
          const { kind, what } =
            steps.length === 1
              ? mapBookingType(first.booking_type, first.machine_id)
              : {
                  kind: "session" as const,
                  what: stackLabel(steps.map((s) => s.machine_id)),
                };
          // Cancelling a stack cancels the whole group, so the group is only
          // cancelled (or a no-show) when every station is — a mixed group is
          // a visit that partly happened, and reads as one.
          const cancelled = steps.every((s) => s.status === "cancelled");
          const noShow = steps.every((s) => s.status === "no_show");
          // A cancelled booking or a no-show was never walked into, so don't
          // let a clock overlap borrow another visit's times for it.
          const entry =
            cancelled || noShow ? null : visitTimesFor(visitSessions, first);
          // Summed across the group rather than read off the first row: the
          // charge hangs off the first booking, but a per-row refund (an early
          // leave, a partial cancellation fee) lands on the row it belongs to.
          const credits = steps.reduce(
            (total, s) => total + (creditsByBooking[s.id] ?? 0),
            0,
          );
          return {
            sortKey: `${first.booking_date}T${first.start_time}`,
            visit: {
              what,
              date: shortDate(first.booking_date),
              tin: t5(first.start_time),
              tout: t5(last.end_time),
              kind,
              scanIn: entry?.scanIn ?? null,
              scanOut: entry?.scanOut ?? null,
              noShow,
              cancelled,
              credits: credits > 0 ? credits : null,
              cancelledOn: cancelled
                ? (cancelDateByBooking[first.id] ?? null)
                : null,
            },
          };
        });

      const walkinVisits = (
        (walkinRes.data as
          | {
              started_at: string;
              ended_at: string;
              credits_deducted: number | null;
            }[]
          | null) || []
      ).map((s) => ({
        sortKey: s.started_at,
        visit: {
          what: "walk-in session",
          date: shortDate(s.started_at),
          tin: sastTime(s.started_at),
          tout: sastTime(s.ended_at),
          kind: "session",
          // A walk-in's booked window IS the scanned window.
          scanIn: sastTime(s.started_at),
          scanOut: sastTime(s.ended_at),
          noShow: false,
          cancelled: false,
          credits: s.credits_deducted ?? null,
        },
      }));

      setVisits(
        [...bookingVisits, ...walkinVisits]
          .sort((a, b) => (a.sortKey < b.sortKey ? 1 : -1))
          .slice(0, 50)
          .map((v) => v.visit),
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [session, tick]);

  const signOut = useCallback(async () => {
    // Forget any Face ID credential first — the refresh token we stored is
    // revoked by sign-out, so it must not linger. Dynamically imported: it's
    // only reachable from a real sign-out action, and faceIdLogin.ts pulls
    // in the full Supabase client (see authClient.ts for why that matters).
    const { disableFaceIdLogin } = await import("./faceIdLogin");
    await disableFaceIdLogin();
    await authClient.signOut();
  }, []);

  return {
    loading,
    loadErrors,
    session,
    userId: session?.user?.id ?? null,
    name,
    initials: initialsFrom(name),
    email,
    mobile,
    phoneVerified,
    emailVerified,
    profileLoaded,
    dob,
    dobIso,
    preferredName,
    gender,
    heightCm,
    avatar,
    credits,
    plan,
    planBilling,
    planType,
    planCommitmentEnd,
    planRenews,
    planAutoRenew,
    inbodyScore,
    inbodyData,
    upcoming,
    invoices,
    redeemedCodes,
    visits,
    liveFree,
    refresh,
    signOut,
  };
}
